import AgoraRTC from 'agora-rtc-sdk'

import EventBus, { eventTypes } from '@/utils/EventBus'

class Agora {
  client: AgoraRTC.Client
  appID = '4cbdc073262a4f9486c5defe51323b8c'
  agoraId!: string
  channel: string | null = null
  localStream!: AgoraRTC.Stream
  streams: Map<string, AgoraRTC.Stream> = new Map()
  ready: boolean = false
  readyCallbacks: (() => void)[] = []
  isJoining: boolean = false
  switchingGuestId: string | null = null
  channelJoinQueue: { channel: string; uid: string }[] = []
  streamAudioTohs: Map<string, number> = new Map()

  constructor() {
    this.client = AgoraRTC.createClient({ mode: 'rtc', codec: 'h264' })
  }

  // Initialize Agora here so we can explicitly call this from App.vue
  // and make sure it is called once at the right time.
  init() {
    AgoraRTC.Logger.enableLogUpload();
    AgoraRTC.Logger.setLogLevel(4); // 2 = warning

    this.client.init(
      this.appID,
      () => {
        const callbacks = this.readyCallbacks
        this.readyCallbacks = []
        this.ready = true
        callbacks.forEach(cb => cb())
      },
      err => {
        console.error(err)
      }
    )
    this.client.on('stream-added', evt => {
      var remoteStream = evt.stream
      var id = remoteStream.getId()
      console.log(`[AgoraRTC] stream-added remote-uid: ${id}`)
      if (id !== this.agoraId) {
        const isSwitchingGuest =
          this.switchingGuestId === id ||
          this.switchingGuestId === this.agoraId ||
          (!!this.switchingGuestId && id.includes('waiter'))
        this.streams.set(id, remoteStream)
        this.client.subscribe(remoteStream, { video: true, audio: isSwitchingGuest }, err => {
          console.log(`[AgoraRTC] Stream ${id} subscribe failed (ready: ${this.ready})`, err)
        })
        if (isSwitchingGuest) {
          this.switchingGuestId = null
        }
        EventBus.$emit(eventTypes.streamAdded)
      }
    })

    this.client.on('stream-removed', evt => {
      console.log('[AgoraRTC] stream-removed. event:', evt)

      const id = evt.stream.getId()

      // removing the agora created player div
      var element = document.getElementById('player_' + id)
      element?.parentNode?.removeChild(element)

      this.streams.delete(id)
    })

    this.client.on('stream-subscribed', evt => {
      var remoteStream = evt.stream
      var id = remoteStream.getId()
      this.play(id)
    })

    this.onBeforeUnload = this.onBeforeUnload.bind(this)
    window.addEventListener('beforeunload', this.onBeforeUnload)
  }

  async isReady() {
    if (this.ready) {
      return Promise.resolve()
    } else {
      return new Promise(resolve => this.readyCallbacks.push(resolve))
    }
  }

  async join(channel: string, uid: string, switchingGuestId: string | null = null) {
    // Make sure we're not trying to join multiple times in parallel
    if (this.isJoining) {
      console.log(`[AgoraRTC] Already joining another channel, ${channel} as ${uid} added to queue`)
      this.channelJoinQueue.push({ channel, uid })
      return
    } else {
      console.log(`[AgoraRTC] Joining channel ${channel} as ${uid}`)
      this.isJoining = true
    }

    this.agoraId = uid
    this.switchingGuestId = switchingGuestId

    await this.leave()

    return new Promise((resolve, reject) => {
      this.client.join(
        null,
        channel,
        uid,
        uid => {
          console.log(`[AgoraRTC] Joined channel ${channel} as ${uid}`)
          this.isJoining = false
          this.channel = channel
          this.channelJoinQueue = this.channelJoinQueue.filter(c => c.channel !== channel)

          if (this.channelJoinQueue.length > 0) {
            const { channel, uid } = this.channelJoinQueue[0]
            this.join(channel, uid)
          } else {
            this.createStream()
            resolve()
          }
        },
        err => {
          console.error(`[AgoraRTC] client ${uid} join ${channel} failed`, err)
          this.isJoining = false
          if (uid === this.switchingGuestId) {
            this.switchingGuestId = null
          }
          reject(err)
        }
      )
    })
  }

  async leave() {
    return new Promise(resolve => {
      if (this.channel) {
        this.client.leave(() => {
          this.channel = null
          this.streams = new Map()
          resolve()
        })
      } else {
        resolve()
      }
    })
  }

  createStream(elementId: string | undefined = 'local_stream') {
    const localStream = AgoraRTC.createStream({
      streamID: this.agoraId,
      audio: true,
      video: true,
      screen: false,
    })
    localStream.setVideoProfile('720p_6')
    localStream.init(
      // this causes permissions popup
      () => {
        // play stream with html element id "local_stream"
        localStream.play(elementId)
        this.publish()
      },
      function(err) {
        console.error('init local stream failed ', err)
      }
    )
    this.localStream = localStream
  }

  publish() {
    this.client.publish(this.localStream, err => {
      console.log('publish failed')
      console.error(err)
    })
  }

  play(id: string) {
    this.streams.get(id)?.play(`video_play_${id}`)
  }

  stop(id: string) {
    this.streams.get(id)?.stop()
  }

  setVolume(id: string, volume: number) {
    this.streams.get(id)?.setAudioVolume(volume)
  }

  //
  // Audio was unstable, sometimes the waiter or guest was not audible even though earlier they
  // were and there are no signs of hardware issues. Since the codebase is littered with async
  // calls, roundtrips through Firebase on one side and strict state management required for Agora
  // (you can't subscribe to a video that doesn't exist yet for instance) it is very likely this
  // method is called while the app is still in an incorrrect state.
  //
  // To cushion the impact of those bugs without the need to do a major refactor I've added retry
  // logic to this method.
  //
  streamAudio(id: string, audio: boolean) {
    const clear = () => {
      clearTimeout(this.streamAudioTohs.get(id))
      this.streamAudioTohs.delete(id)
    }

    const tryStreamAudio = () => {
      const stream = this.streams.get(id)
      if (!stream) {
        // We might end up here before the client had a chance to publish their stream
        retryStreamAudio()
        return
      }

      this.client.subscribe(stream, { video: true, audio }, err => {
        console.log(`[AgoraRTC] Stream ${id} subscribe failed (ready: ${this.ready})`, err)
        if (!retryStreamAudio()) {
          alert(`Er lijkt iets fout te gaan met het geluid.\nProbeer het later nog eens.\n\n${err}`)
        }
      })
    }

    let attempt = 0
    const retryStreamAudio = () => {
      if (attempt > 0) {
        console.log(`[AgoraRTC] Retry streamAudio ${id} ${audio} attempt ${attempt}`)
      }

      if (attempt++ <= 3) {
        this.streamAudioTohs.set(id, setTimeout(() => {
          clear()
          tryStreamAudio()
        }, 500 * attempt))
        return true
      } else {
        return false
      }
    }

    // Clear possible pending timeouts from previous invocations of streamAudio
    clear()
    retryStreamAudio()
  }

  muteAudio() {
    this.localStream.muteAudio()
  }

  unmuteAudio() {
    this.localStream.unmuteAudio()
  }

  onBeforeUnload() {
    this.destroy()
  }

  destroy() {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
    this.client && this.client.unpublish(this.localStream)
    this.localStream && this.localStream.close()
    this.client &&
      this.client.leave(
        () => {
          // nothing
        },
        () => {
          console.log('Client failed to leave.')
        }
      )
  }
}

export default new Agora()
